import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header/Header';
import Background from '../components/Background/Background';
import '../styles/DoctorChangeDetailPage.css';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchWithAuth } from '../api/fetchWithAuth';

type DetailItem = {
  itemId: string;
  action: string;
  medicationIdentifier: string;
  proposedPayload: any;
  decision: 'APPROVE' | 'DENY' | null;
  reasonCode: string | null;
  reasonDisplayLabel: string | null;
  reviewedAt: string | null;
  notesPlaintext: string | null;
};

type RegistrationDetails = {
  demographics?: {
    name?: string;
    ssn?: string;
    phone?: string;
  };
  deliveryType?: string;
  deliveryDetails?: {
    address?: string;
    postalCode?: string;
    city?: string;
    store?: string;
    dispenser?: string;
    priority?: string;
  };
};

type DetailResponse = {
  workOrderId: string;
  patientId: string;
  ssn: string;
  status: string;
  orderType: string;
  createdAt: string;
  registrationDetails?: RegistrationDetails | null;
  items: DetailItem[];
};

const actionLabel: Record<string, string> = {
  ADD: 'Nýtt lyf',
  EDIT: 'Breyting',
  REMOVE: 'Lyf tekið úr skömmtun',
  PAUSE: 'Stöðva skömmtun',
  RESUME: 'Endurvekja skömmtun',
};

const statusLabel: Record<string, string> = {
  APPROVED: 'SAMÞYKKT',
  REJECTED: 'HAFNAÐ',
  PARTIALLY_APPROVED: 'SAMÞYKKT AÐ HLUTA',
  PENDING: 'Í VINNSLU',
  CANCELLED: 'AFTURKALLAÐ',
  COMPLETED_EXTERNALLY: 'LOKIÐ',
};

export default function PharmacistFinishedChangeDetailPage() {
  const { workOrderId = '' } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'new-changes' | 'old-changes'>('old-changes');

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [wo, setWo] = useState<DetailResponse | null>(null);

  const API_BASE = 'https://api-staging.mitoflux.is';
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setErr('');

        const res = await fetchWithAuth(
          getAccessTokenSilently,
          `${API_BASE}/api/pharmacy/work-orders/${encodeURIComponent(workOrderId)}`
        );
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`API ${res.status}: ${text}`);
        }

        const data = (await res.json()) as DetailResponse;
        setWo(data);
      } catch (e: any) {
        console.error(e);
        setErr(e?.message || 'Villa kom upp');
      } finally {
        setLoading(false);
      }
    })();
  }, [workOrderId, getAccessTokenSilently]);

  const rejectedCount = useMemo(() => {
    if (!wo) return 0;
    return wo.items.filter((i) => i.decision === 'DENY').length;
  }, [wo]);

  return (
    <>
      <Header />

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'new-changes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('new-changes');
            navigate('/pharmacist');
          }}
        >
          Nýjar breytingar á skömmtunum
        </button>

        <button
          className={`tab ${activeTab === 'old-changes' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('old-changes');
            navigate('/pharmacist/finished');
          }}
        >
          Fyrri breytingar
        </button>
      </div>

      <Background>
        <div className="doc-detail">
          <button className="doc-back" onClick={() => navigate(-1)}>
            ← Til baka
          </button>

          {loading && <div style={{ color: '#fff' }}>Hleð…</div>}
          {err && <div style={{ color: '#fff' }}>{err}</div>}

          {!loading && wo && (
            <>
              <div className="doc-detail-head">
                <div className="doc-name">
                  {wo.registrationDetails?.demographics?.name
                    ? `${wo.registrationDetails.demographics.name} — ${wo.ssn}`
                    : `Kennitala: ${wo.ssn}`}
                </div>
                {wo.registrationDetails && (
                  <div className="doc-sub">
                    {wo.registrationDetails.demographics?.phone && (
                      <>Sími: {wo.registrationDetails.demographics.phone} • </>
                    )}
                    {wo.registrationDetails.deliveryType === 'sent' ||
                    wo.registrationDetails.deliveryType === 'HOME_DELIVERY'
                      ? 'Sent'
                      : 'Sótt'}
                    {wo.registrationDetails.deliveryDetails?.store && (
                      <> • {wo.registrationDetails.deliveryDetails.store}</>
                    )}
                    {wo.registrationDetails.deliveryDetails?.address && (
                      <>
                        {'·'} • {wo.registrationDetails.deliveryDetails.address}
                        {wo.registrationDetails.deliveryDetails.postalCode &&
                          `, ${wo.registrationDetails.deliveryDetails.postalCode}`}
                        {wo.registrationDetails.deliveryDetails.city &&
                          ` ${wo.registrationDetails.deliveryDetails.city}`}
                      </>
                    )}
                    {wo.registrationDetails.deliveryDetails?.dispenser && (
                      <>
                        {'·'}•{'·'}
                        {wo.registrationDetails.deliveryDetails.dispenser}
                      </>
                    )}
                    {wo.registrationDetails.deliveryDetails?.priority && (
                      <>
                        {'·'}•{'·'}
                        {wo.registrationDetails.deliveryDetails.priority === 'flyti'
                          ? 'Flýti'
                          : 'Venjuleg'}
                      </>
                    )}
                  </div>
                )}
                <div className="doc-sub">
                  {statusLabel[wo.status] ?? wo.status} • Fjöldi hafnaðra breytinga: {rejectedCount}
                  {'·'} • {new Date(wo.createdAt).toLocaleString()}
                </div>
              </div>

              <div className="doc-rows">
                {wo.items.map((r) => (
                  <div
                    key={r.itemId}
                    className={
                      'doc-row ' +
                      (r.decision === 'APPROVE' ? 'ok' : r.decision === 'DENY' ? 'bad' : 'pending')
                    }
                  >
                    <div className="doc-row-left">
                      <div className="doc-row-name">{r.medicationIdentifier}</div>
                      <div className="doc-row-meta">{actionLabel[r.action] ?? r.action}</div>
                    </div>

                    <div className="doc-row-mid">
                      {/* show instruction if present in proposedPayload */}
                      {r.proposedPayload?.instruction ? (
                        <span className="doc-pill">{r.proposedPayload.instruction}</span>
                      ) : (
                        <span>—</span>
                      )}
                    </div>

                    <div className="doc-row-right">
                      {r.decision === 'APPROVE' && <span className="doc-chip ok">✓ Samþykkt</span>}
                      {r.decision === 'DENY' && (
                        <span className="doc-chip bad" title={r.reasonCode ?? ''}>
                          ✕ Hafnað
                        </span>
                      )}
                      {!r.decision && <span className="doc-chip pending">—</span>}
                    </div>

                    {r.decision === 'DENY' && (
                      <div className="doc-reason">
                        Ástæða: {r.reasonDisplayLabel ?? r.reasonCode ?? '—'}
                        {r.notesPlaintext ? ` • ${r.notesPlaintext}` : ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Background>
    </>
  );
}
